<template>
  <!-- Parameters of the side work check (GET/PUT /registry/settings; PUT takes the whole object). -->
  <form
    class="ai-sws"
    @submit.prevent="save"
  >
    <p
      v-if="loading"
      class="ai-sws__line is-muted"
    >
      <span class="ai-spinner ai-spinner--small" />
      Загружаю параметры…
    </p>
    <template v-else-if="form">
      <p class="ai-sws__line is-muted">
        Изменения действуют со следующей проверки. «По умолчанию» возвращает рекомендуемое значение.
      </p>

      <fieldset
        v-for="group in groups"
        :key="group.title"
        class="ai-sws__group"
      >
        <legend>{{ group.title }}</legend>
        <div
          v-for="field in group.lists"
          :key="field.key"
          class="ai-field"
        >
          <div class="ai-sws__head">
            <label
              class="ai-field__label"
              :for="`ai-sws-${field.key}`"
            >
              {{ field.label }}
            </label>
            <button
              v-if="!isDefault(field.key)"
              type="button"
              class="ai-link-button ai-sws__default"
              @click="resetField(field.key)"
            >
              <ai-icon
                name="reset"
                :size="13"
              />
              По умолчанию
            </button>
          </div>
          <textarea
            :id="`ai-sws-${field.key}`"
            v-model="form[field.key]"
            class="ai-input"
            :rows="field.rows || 3"
            :placeholder="field.placeholder"
          />
          <span class="ai-field__hint">{{ field.hint }}</span>
        </div>

        <!-- Projects: how codes are recognised and where the network drives point. -->
        <template v-if="group.key === 'projects'">
          <div class="ai-sws__head">
            <label class="ai-check">
              <input
                v-model="form.learnCodeFormats"
                type="checkbox"
              >
              <span>Брать форматы шифров из реестра проектов</span>
            </label>
            <button
              v-if="!isDefault('learnCodeFormats')"
              type="button"
              class="ai-link-button ai-sws__default"
              @click="resetField('learnCodeFormats')"
            >
              <ai-icon
                name="reset"
                :size="13"
              />
              По умолчанию
            </button>
          </div>

          <div class="ai-field">
            <div class="ai-sws__head">
              <span class="ai-field__label">Сетевые диски</span>
              <button
                v-if="!isDefault('drives')"
                type="button"
                class="ai-link-button ai-sws__default"
                @click="resetField('drives')"
              >
                <ai-icon
                  name="reset"
                  :size="13"
                />
                По умолчанию
              </button>
            </div>
            <div
              v-for="(drive, index) in form.drives"
              :key="index"
              class="ai-sws__drive"
            >
              <input
                v-model.trim="drive.letter"
                class="ai-input ai-sws__letter"
                maxlength="2"
                placeholder="P:"
                :aria-label="`Буква диска ${index + 1}`"
              >
              <input
                v-model.trim="drive.path"
                class="ai-input"
                maxlength="400"
                placeholder="\\server\projects"
                :aria-label="`Сетевой путь диска ${index + 1}`"
              >
              <button
                type="button"
                class="ai-icon-button is-small"
                title="Убрать диск"
                @click="form.drives.splice(index, 1)"
              >
                <ai-icon
                  name="close"
                  :size="14"
                />
              </button>
            </div>
            <button
              type="button"
              class="ai-link-button ai-sws__add"
              :disabled="form.drives.length >= 26"
              @click="form.drives.push({ letter: '', path: '' })"
            >
              <ai-icon
                name="plus"
                :size="14"
              />
              Добавить диск
            </button>
            <span class="ai-field__hint">
              Буква диска на компьютерах сотрудников и сетевая папка, которую он открывает: так путь P:\15-2024
              совпадает с папкой проекта \\server\projects\15-2024.
            </span>
          </div>
        </template>

        <!-- Time and scoring. -->
        <template v-if="group.key === 'score'">
          <div class="ai-field">
            <div class="ai-sws__head">
              <label class="ai-check">
                <input
                  v-model="form.workHours.enabled"
                  type="checkbox"
                >
                <span>Учитывать рабочее время</span>
              </label>
              <button
                v-if="!isDefault('workHours')"
                type="button"
                class="ai-link-button ai-sws__default"
                @click="resetField('workHours')"
              >
                <ai-icon
                  name="reset"
                  :size="13"
                />
                По умолчанию
              </button>
            </div>
            <div
              class="ai-sws__hours"
              :class="{ 'is-off': !form.workHours.enabled }"
            >
              <label class="ai-sws__inline">
                <span>с</span>
                <input
                  v-model="form.workHours.start"
                  type="time"
                  class="ai-input ai-sws__time"
                  :disabled="!form.workHours.enabled"
                >
              </label>
              <label class="ai-sws__inline">
                <span>до</span>
                <input
                  v-model="form.workHours.end"
                  type="time"
                  class="ai-input ai-sws__time"
                  :disabled="!form.workHours.enabled"
                >
              </label>
              <span class="ai-sws__days">
                <label
                  v-for="day in weekdays"
                  :key="day.value"
                  class="ai-sws__day"
                  :class="{ 'is-on': form.workHours.days.includes(day.value) }"
                >
                  <input
                    v-model="form.workHours.days"
                    type="checkbox"
                    :value="day.value"
                    :disabled="!form.workHours.enabled"
                  >
                  {{ day.label }}
                </label>
              </span>
            </div>
            <span class="ai-field__hint">Работа над файлами вне рабочего времени добавляет балл находке.</span>
          </div>

          <div class="ai-sws__grid">
            <div
              v-for="field in numbers"
              :key="field.key"
              class="ai-field"
            >
              <div class="ai-sws__head">
                <label
                  class="ai-field__label"
                  :for="`ai-sws-${field.key}`"
                >
                  {{ field.label }}
                </label>
                <!-- A narrow grid column: the icon alone, the text is in the tooltip. -->
                <button
                  v-if="!isDefault(field.key)"
                  type="button"
                  class="ai-link-button ai-sws__default"
                  title="Вернуть по умолчанию"
                  aria-label="Вернуть по умолчанию"
                  @click="resetField(field.key)"
                >
                  <ai-icon
                    name="reset"
                    :size="13"
                  />
                </button>
              </div>
              <input
                :id="`ai-sws-${field.key}`"
                v-model.number="form[field.key]"
                type="number"
                class="ai-input"
                :min="field.min"
                :max="field.max"
                :step="field.step"
              >
              <span class="ai-field__hint">{{ field.hint }}</span>
            </div>
          </div>
        </template>
      </fieldset>

      <p
        v-if="error"
        class="ai-sws__line is-error"
        role="alert"
      >
        {{ error }}
      </p>
      <p
        v-if="savedAt"
        class="ai-sws__line is-muted"
        role="status"
      >
        Сохранено.
      </p>

      <div class="ai-sws__actions">
        <button
          type="button"
          class="ai-button"
          :disabled="!dirty || saving"
          @click="revert"
        >
          Отменить изменения
        </button>
        <button
          type="submit"
          class="ai-button is-primary"
          :disabled="!dirty || saving"
        >
          <span
            v-if="saving"
            class="ai-spinner ai-spinner--small"
          />
          Сохранить
        </button>
      </div>
    </template>
    <p
      v-else
      class="ai-sws__line is-error"
    >
      {{ error }}
    </p>
  </form>
</template>

<script>
  import * as api from '@/api/methods/assistant';
  import AiIcon from './AiIcon.vue';

  const LISTS = {
    extensions: {
      key: 'extensions',
      label: 'Форматы проектов',
      hint: 'Файлы, которые бывают только в проектах; без точки, по одному в строке: dwg, rvt, pln… '
        + 'Такой файл в папке вне реестра — уже находка.',
      placeholder: 'dwg',
      rows: 4,
    },
    documentExtensions: {
      key: 'documentExtensions',
      label: 'Документы',
      hint: 'Без точки, по одному в строке: docx, xlsx, pdf… Документ вне папок проектов становится находкой '
        + 'только вместе с другим признаком: шифром, отправкой, чужим автором, работой вне рабочего времени '
        + 'или дольше порога.',
      placeholder: 'docx',
      rows: 4,
    },
    cadPrograms: {
      key: 'cadPrograms',
      label: 'Рабочие программы',
      hint: 'Имя процесса без .exe, по одному в строке: acad, revit, winword, excel… По заголовкам их окон '
        + 'видно, над каким файлом и сколько работали.',
      placeholder: 'acad',
      rows: 4,
    },
    codeTemplates: {
      key: 'codeTemplates',
      label: 'Форматы шифров',
      hint: '9 — цифра, * — буква, остальные символы как есть; пусто — форматы берутся из шифров реестра',
      placeholder: '99-9999',
    },
    ignoredFolders: {
      key: 'ignoredFolders',
      label: 'Не проверять папки',
      hint: 'Системные и служебные папки, по одной в строке; * — любая папка, например C:\\Users\\*\\AppData',
      placeholder: 'C:\\Windows',
      rows: 4,
    },
    internalDomains: {
      key: 'internalDomains',
      label: 'Внутренние домены',
      hint: 'Почта и контакты на этих доменах — внутренняя переписка, а не отправка наружу: example.com',
      placeholder: 'example.com',
    },
  };
  const GROUPS = [
    {
      key: 'files',
      title: 'Файлы и программы',
      lists: [LISTS.extensions, LISTS.documentExtensions, LISTS.cadPrograms],
    },
    { key: 'projects', title: 'Проекты', lists: [LISTS.codeTemplates, LISTS.ignoredFolders] },
    { key: 'recipients', title: 'Получатели', lists: [LISTS.internalDomains] },
    { key: 'score', title: 'Время и оценка', lists: [] },
  ];
  const NUMBERS = [
    {
      key: 'longWorkHours',
      label: 'Долгая работа, ч',
      min: 0.25,
      max: 24,
      step: 0.25,
      hint: 'Столько часов работы над одним файлом за день добавляют балл.',
    },
    {
      key: 'minScore',
      label: 'Порог находки, баллов',
      min: 1,
      max: 10,
      step: 1,
      hint: 'Файлы с меньшей суммой баллов не попадают в находки.',
    },
  ];
  const LIST_KEYS = Object.keys(LISTS);
  const WEEKDAYS = ['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Вс'].map((label, index) => ({ label, value: index + 1 }));
  const TIME = /^([01]\d|2[0-3]):[0-5]\d$/;

  const lines = (text) =>
    String(text || '')
      .split(/\r?\n/)
      .map((line) => line.trim())
      .filter(Boolean);
  const copy = (value) => JSON.parse(JSON.stringify(value));

  function formOf(settings) {
    const form = copy(settings);
    LIST_KEYS.forEach((key) => {
      form[key] = (settings[key] || []).join('\n');
    });
    form.drives = copy(settings.drives || []);
    form.workHours = { enabled: false, start: '09:00', end: '18:00', days: [], ...copy(settings.workHours || {}) };
    return form;
  }

  // The whole settings object of the API.
  function settingsOf(form) {
    const settings = copy(form);
    LIST_KEYS.forEach((key) => {
      settings[key] = lines(form[key]);
    });
    settings.drives = form.drives
      .map((drive) => ({ letter: drive.letter.trim().toUpperCase(), path: drive.path.trim() }))
      .filter((drive) => drive.letter || drive.path);
    settings.workHours.days = [...form.workHours.days].sort();
    return settings;
  }

  export default {
    name: 'SideworkSettings',
    components: { AiIcon },
    data: () => ({
      loading: true,
      saved: null,
      defaults: null,
      form: null,
      saving: false,
      savedAt: null,
      error: '',
      groups: GROUPS,
      numbers: NUMBERS,
      weekdays: WEEKDAYS,
    }),
    computed: {
      current() {
        return settingsOf(this.form);
      },
      dirty() {
        return !!this.form && JSON.stringify(this.current) !== JSON.stringify(settingsOf(formOf(this.saved)));
      },
    },
    created() {
      this.load();
    },
    methods: {
      apply(data) {
        this.saved = data.settings || {};
        this.defaults = data.defaults || {};
        this.form = formOf(this.saved);
      },
      async load() {
        this.loading = true;
        this.error = '';
        try {
          const { data } = await api.getSideworkSettings();
          this.apply(data);
        } catch (error) {
          this.error = api.errorText(error, 'Не удалось загрузить параметры');
        } finally {
          this.loading = false;
        }
      },
      isDefault(key) {
        if (!this.defaults || !(key in this.defaults)) return true;
        const fallback = settingsOf(formOf(this.defaults));
        return JSON.stringify(this.current[key]) === JSON.stringify(fallback[key]);
      },
      resetField(key) {
        this.form[key] = formOf(this.defaults)[key];
        this.savedAt = null;
      },
      revert() {
        this.form = formOf(this.saved);
        this.error = '';
      },
      validate(settings) {
        const { workHours } = settings;
        if (workHours.enabled) {
          if (!TIME.test(workHours.start) || !TIME.test(workHours.end)) return 'Укажите начало и конец рабочего дня.';
          if (!workHours.days.length) return 'Отметьте рабочие дни недели.';
        }
        const wrong = NUMBERS.find((field) => {
          const value = settings[field.key];
          return typeof value !== 'number' || Number.isNaN(value) || value < field.min || value > field.max;
        });
        if (wrong) return `«${wrong.label}»: допустимо от ${wrong.min} до ${wrong.max}.`;
        if (!Number.isInteger(settings.minScore)) return '«Порог находки»: укажите целое число.';
        const drive = settings.drives.find((item) => !/^[A-Z]:?$/.test(item.letter) || item.path.length < 3);
        if (drive) return `Сетевой диск «${drive.letter || '?'}»: укажите букву (P:) и сетевую папку.`;
        return '';
      },
      async save() {
        const settings = this.current;
        this.error = this.validate(settings);
        this.savedAt = null;
        if (this.error) return;
        this.saving = true;
        try {
          const { data } = await api.saveSideworkSettings(settings);
          this.apply(data);
          this.savedAt = Date.now();
        } catch (error) {
          this.error = api.errorText(error, 'Не удалось сохранить параметры');
        } finally {
          this.saving = false;
        }
      },
    },
  };
</script>

<style lang="scss" scoped>
  .ai-sws {
    display: flex;
    flex-direction: column;
    gap: 12px;
  }

  .ai-sws__line {
    display: flex;
    align-items: center;
    gap: 8px;
    margin: 0;
    color: var(--ui-text);
    font-size: 13px;
    line-height: 1.45;

    &.is-muted {
      color: var(--ui-text-muted);
    }

    &.is-error {
      color: var(--ui-danger);
    }
  }

  .ai-sws__group {
    display: flex;
    flex-direction: column;
    gap: 12px;
    min-width: 0;
    margin: 0;
    padding: 12px;
    border: 1px solid var(--ui-border);
    border-radius: var(--ui-radius);

    // As in the assistant settings: the legend is a header row inside the frame.
    legend {
      float: left;
      width: 100%;
      padding: 0;
      color: var(--ui-text);
      font-weight: 500;
    }
  }

  .ai-sws__head {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: space-between;
    gap: 4px 10px;
  }

  .ai-widget .ai-sws__default {
    font-size: 12px;
  }

  .ai-sws__drive {
    display: flex;
    align-items: center;
    gap: 6px;
  }

  .ai-widget .ai-sws__letter {
    flex: 0 0 56px;
    width: 56px;
    text-transform: uppercase;
  }

  .ai-sws__add {
    align-self: flex-start;
  }

  .ai-sws__hours {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 8px 12px;

    &.is-off {
      opacity: 0.6;
    }
  }

  .ai-sws__inline {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    margin: 0;
    color: var(--ui-text-muted);
    font-size: 13px;
  }

  .ai-widget .ai-sws__time {
    width: auto;
  }

  .ai-sws__days {
    display: inline-flex;
    flex-wrap: wrap;
    gap: 4px;
  }

  // A weekday toggle: the checkbox is hidden, the label is the chip.
  .ai-sws__day {
    position: relative;
    margin: 0;
    padding: 3px 9px;
    border: 1px solid var(--ui-border);
    border-radius: 999px;
    color: var(--ui-text-muted);
    font-size: 12px;
    cursor: pointer;

    input {
      position: absolute;
      opacity: 0;
      pointer-events: none;
    }

    &.is-on {
      border-color: var(--ui-primary);
      background: var(--ui-primary-soft);
      color: var(--ui-primary);
    }

    &:focus-within {
      box-shadow: var(--ui-focus);
    }
  }

  .ai-sws__grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
    gap: 10px 12px;
  }

  .ai-sws__actions {
    display: flex;
    flex-wrap: wrap;
    justify-content: flex-end;
    gap: 8px;
  }
</style>
